
import React, { useEffect, useState } from "react";
import "../assets/css/GroupLoanReport.css"; // keep your custom CSS
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { fetchUserProfile } from "../redux/slices/branch/userProfileSlice";
import { useDispatch, useSelector } from "react-redux";
import { FaRupeeSign } from "react-icons/fa";
import { toast } from "react-toastify";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";

const GroupLoanDocuments = () => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.userProfile);

  /* ---------------- STATES ---------------- */
  const [loans, setLoans] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loanTypeFilter, setLoanTypeFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [groups, setGroups] = useState([]);
  const [centers, setCenters] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [selectedCenter, setSelectedCenter] = useState(null);

  const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50, 100, "All"];

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  /* ---------------- FETCH CENTERS ---------------- */
  useEffect(() => {
    if (!profile?.id) return;

    const fetchCenters = async () => {
      try {
        const res = await axios.get(
          `https://payyug.in/api/group/centre?LoanofficerId=${profile.id}&limit=10000`,
        );
        setCenters(res.data?.data || []);
      } catch (error) {
        console.error("Center fetch error:", error);
      }
    };

    fetchCenters();
  }, [profile?.id]);

  /* ---------------- FETCH GROUPS ---------------- */
  useEffect(() => {
    if (!selectedCenter) return;

    const fetchGroups = async () => {
      try {
        const res = await axios.get(
          `https://payyug.in/api/groupupdate/getall?LoanofficerId=${profile?.id}&CenterId=${selectedCenter}&limit=10000`,
        );
        setGroups(res.data?.data || []);
      } catch (error) {
        console.error("Group fetch error:", error);
      }
    };

    fetchGroups();
  }, [selectedCenter, profile?.id]);

  /* ---------------- FETCH EMI ---------------- */
  const fetchLoanReport = async (page = 1) => {
    if(!profile?.superAdminId) return;
    try {
      setLoading(true);

      const res = await axios.get(
        "https://payyug.in/api/group-loan/group/list",
        {
          params: {
            superAdminId: profile.superAdminId,
            officerId: profile.id,
            groupId: selectedGroup,
            centerId: selectedCenter,
            page,
            loanType: loanTypeFilter || undefined,
            limit: itemsPerPage === "All" ? 100000 : itemsPerPage,
          },
        },
      );
      setLoans(res.data.data || []);
      setCurrentPage(res.data.currentPage);
      setTotalPages(res.data.totalPages || 1);
    } catch (error) {
      console.error("Error fetching loan report:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profile?.superAdminId) {
      fetchLoanReport(currentPage);
    }
  }, [currentPage, itemsPerPage, profile]);


  const navigateTo = (path, loan) => {
    navigate(path, {
      state: {
        loan,
        customerId: loan?.member?.userId,
        superAdminId: loan.superAdminId,
      },
    });
  };

  const filteredLoans = loans.filter((loan) => {
    const searchMatch =
      !searchQuery ||
      `
    ${loan.loanAccountNumber}
    ${loan.loanType}
    ${loan.memberName}
    ${loan.groupLeaderName}
    ${loan.groupName}
  `
        .toLowerCase()
        .includes(searchQuery.toLowerCase());

    return searchMatch;
  });

  /* ---------------- FILTER SUBMIT ---------------- */
  const handleFilter = () => {
    setCurrentPage(1);
    fetchLoanReport(1);
  };

  if (loading) {
    return (
      <div className="loadingWrapper">
        <div className="spinner"></div>
        <p>Loading loan documents...</p>
      </div>
    );
  }

  return (
    <div className="gledr-container">
      {/* Back wrapper */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 16px",
          backgroundColor: "#fff",
          border: "1px solid #e0e0e0",
          borderRadius: "8px",
          marginBottom: "20px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        }}
      >
        <ArrowLeftIcon
          onClick={() => navigate(-1)}
          style={{
            cursor: "pointer",
            padding: "4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#f5f5f5")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        />

        <h2
          style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: "600",
            color: "#333",
          }}
        >
          Group Loan Documents
        </h2>

        <ArrowRightIcon
          onClick={() => navigate(1)}
          style={{
            cursor: "pointer",
            padding: "4px",
            borderRadius: "4px",
            transition: "background-color 0.2s",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#f5f5f5")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "transparent")
          }
        />
      </div>

      {/* FILTER */}
      <div className="gledr-filterBox">
        <div className="gledr-filter-title">Filter By</div>

        <div className="gledr-filter-wrapper">
          <div className="gledr-filter-item">
            <div className="gledr-filter">
              <label>Loan Type</label>
              <select
                value={loanTypeFilter}
                onChange={(e) => setLoanTypeFilter(e.target.value)}
              >
                <option value="">All Types</option>
                <option value="FIXED">Fixed Loan</option>
                <option value="INDIVIDUAL">Individual Loan</option>
              </select>
            </div>

            <div className="gledr-filter">
              <label>Select Center</label>
              <select
                value={selectedCenter}
                onChange={(e) => {
                  setSelectedCenter(e.target.value);
                  setSelectedGroup("");
                }}
              >
                <option value="">All Centers</option>
                {centers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.CenterName}
                  </option>
                ))}
              </select>
            </div>

            <div className="gledr-filter">
              <label>Select Group</label>
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                disabled={!selectedCenter}
              >
                <option value="">All Groups</option>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.groupname}, {g.groupcode}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="gledr-filter-submit-btn">
            <button className="submit-btn" onClick={handleFilter}>
              submit
            </button>
          </div>
        </div>
      </div>

      <div className="glcr-report-filter">
        <input
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
        />
        <div>
          <label>Show </label>
          <select
            value={itemsPerPage}
            onChange={(e) => {
              const value=e.target.value;
              setItemsPerPage(value==="All"?"All":Number(value));
              setCurrentPage(1);
            }}
          >
            {ITEMS_PER_PAGE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span> entries</span>
        </div>
      </div>


      {/* TABLE */}
      <div className="gledr-report-table-container">
        <table className="gledr-report-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Account No</th>
              <th>Loan Type</th>
              <th>Loan Amount</th>
              <th>Customer name</th>
              <th>Group Leader Name</th>
              <th>Group</th>
              <th>ROI (%)</th>
              <th>Tenure</th>
              <th>Frequency</th>
              <th>Applied Date</th>
              <th>Status</th>
              <th>Documents</th>
              
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="13" align="center">
                  Loading...
                </td>
              </tr>
            ) : filteredLoans.length ? (
              filteredLoans.map((item, i) => {
                return (
                  <tr key={item.id}>
                    <td>
                      {itemsPerPage === "All"
                        ? i + 1
                        : (currentPage - 1) * itemsPerPage + i + 1}
                    </td>
                    <td>{item.loanAccountNumber|| "N/A"}</td>
                    <td>{item.loanType || "N/A"}</td>
                    <td>{item.loanAmount|| "N/A"}</td>
                    <td>{item.memberName|| "N/A"}</td>
                    <td>{item.groupLeaderName|| "N/A"}</td>
                    <td>{item.groupName|| "N/A"}</td>
                    <td>{item.loanRoi|| "N/A"}</td>
                    <td>{item.loanTenure|| "N/A"}</td>
                    <td>{item.loanRecoveryFrequency|| "N/A"}</td>
                    <td>
                      {item.loanAppliedDate
                        ? new Date(item.loanAppliedDate).toLocaleDateString(
                            "en-GB",
                          )
                        : "-"}
                    </td>
                    <td>{item.status}</td>
                    <td>
                         {(item.status.toLowerCase() === "active" || item.status.toLowerCase() === "disbursed") && (
                        <div style={{ display: "flex", gap: "5px" }}>
                          <button className="BranchLoanReport-btn-view-emi" onClick={() => navigateTo(`/grouploan/grouploanreport/GroupLoanEmiCard/${item.id}`, item)}>Loan Card</button>
                          <button className="BranchLoanReport-btn-view-emi" onClick={() => navigateTo(`/GroupLoan/GroupLoanStatement/${item.id}`, item)}>Loan Statement</button>
                          <button className="BranchLoanReport-btn-view-emi" onClick={() => navigateTo(`/GroupLoan/GroupLoanSanction/${item.id}`, item)}>Sanction</button>
                          <button className="BranchLoanReport-btn-view-emi" onClick={() => navigateTo(`/GroupLoan/GroupLoanPromise/${item.id}`, item)}>Promise</button>
                          <button className="BranchLoanReport-btn-view-emi" onClick={() => navigateTo(`/GroupLoan/GroupLoanAgreement/${item.id}`, item)}>Agreement</button>
                        </div>
                      )}
                      {item.status.toLowerCase()==="closed" && (
                          <button className="BranchLoanReport-btn-view-emi" onClick={() => navigate(`/GroupLoan/GroupLoanNOCLetter/${item.id}`, { state: item })}>NOC Letter</button>
                      )}
                      {item.status==="FORCE_CLOSED" && (
                          <button className="BranchLoanReport-btn-view-emi" onClick={() => navigate(`/GroupLoan/GroupLoanNOCLetter/${item.id}`, { state: item })}>NOC Letter</button>
                      )}
                    </td> 
                   
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="13" align="center">
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* PAGINATION */}
      {itemsPerPage !== "All" && (
        <div className="gledr-pagination">
          <button
            disabled={currentPage === 1 || filteredLoans.length === 0}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            ⬅ Prev
          </button>

          <span>
            {filteredLoans.length === 0
              ? "No data"
              : `Page ${currentPage} of ${totalPages}`}
          </span>

          <button
            disabled={currentPage === totalPages || filteredLoans.length === 0}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next ➡
          </button>
        </div>
      )}
    </div>
  );
};

export default GroupLoanDocuments;
